import { useEffect, useState, useCallback, useMemo, type ReactNode, type MouseEvent } from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { Label } from '@/components/ui/label'
import { RichTextEditor, htmlToPlainText } from '@/components/ui/rich-text-editor'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@/components/ui/accordion'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Tag,
  Palette,
  ChevronDownIcon,
  Route,
  Link2,
  ArrowRight,
  ArrowLeft,
  Circle,
  CircleDot,
  Diamond,
  Square,
  Trash2,
  RotateCcw,
  RefreshCw,
  Zap,
  Spline,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { MarkerType } from '@xyflow/react'
import type { Edge, Node } from '@xyflow/react'
import type { EdgeTypeId } from '@/components/canvas/edgeTypes'
import { EDGE_TYPE_OPTIONS } from '@/components/canvas/edgeTypes'
import type { ReshapableEdgeData, PathType, ControlPoint } from '@/types/canvas'

/** Available marker types for edge endpoints */
type MarkerOption = 'none' | 'arrow' | 'arrowclosed' | 'circle' | 'square' | 'diamond'

const MARKER_OPTIONS: { value: MarkerOption; label: string; icon: ReactNode }[] = [
  { value: 'none', label: 'Ninguno', icon: <Circle className="h-3 w-3 opacity-30" /> },
  { value: 'arrow', label: 'Flecha abierta', icon: <ArrowRight className="h-3 w-3" /> },
  { value: 'arrowclosed', label: 'Flecha cerrada', icon: <ArrowRight className="h-3 w-3 fill-current" /> },
  { value: 'circle', label: 'Círculo', icon: <CircleDot className="h-3 w-3" /> },
  { value: 'square', label: 'Cuadrado', icon: <Square className="h-3 w-3" /> },
  { value: 'diamond', label: 'Diamante', icon: <Diamond className="h-3 w-3" /> },
]

const PATH_TYPE_OPTIONS: { value: PathType; label: string; description: string }[] = [
  { value: 'bezier', label: 'Bezier', description: 'Curvas suaves' },
  { value: 'linear', label: 'Lineal', description: 'Líneas rectas' },
  { value: 'step', label: 'Escalonado', description: 'Ángulos rectos' },
  { value: 'smooth', label: 'Suave', description: 'Interpolación Catmull-Rom' },
]

/** Predefined color palette for edges */
const EDGE_COLORS = [
  { value: '#6b7280', name: 'Gris' },      // gray-500
  { value: '#3b82f6', name: 'Azul' },      // blue-500
  { value: '#10b981', name: 'Verde' },     // emerald-500
  { value: '#f59e0b', name: 'Ámbar' },     // amber-500
  { value: '#8b5cf6', name: 'Violeta' },   // violet-500
  { value: '#ef4444', name: 'Rojo' },      // red-500
  { value: '#ec4899', name: 'Rosa' },      // pink-500
  { value: '#06b6d4', name: 'Cyan' },      // cyan-500
]


/** Extended edge data interface for the inspector */
interface EdgeData extends Partial<ReshapableEdgeData> {
  label?: string // Plain text label
  labelHtml?: string // Rich HTML for editing
  description?: string
  descriptionHtml?: string // Rich HTML for description
  animated?: boolean
  color?: string
  strokeWidth?: number
  pathType?: PathType
  controlPoints?: ControlPoint[]
  isEditing?: boolean
}

interface EdgePropertiesPanelProps {
  edge: Edge
  nodes: Node[]
  onChange: (updates: Partial<Edge>) => void
  onDelete?: () => void
  onReverse?: () => void
  /** Called when drag starts (for FloatingPanel integration) */
  onDragStart?: (e: MouseEvent) => void
}

/** Color swatch button for the color picker */
function ColorSwatch({
  color,
  name,
  selected,
  onClick,
}: {
  color: string
  name: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            className={cn(
              'w-6 h-6 rounded-full transition-all border-2',
              selected
                ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110 border-white'
                : 'hover:scale-110 border-transparent'
            )}
            style={{ backgroundColor: color }}
            aria-label={`Color ${name}`}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {name}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default function EdgePropertiesPanel({
  edge,
  nodes,
  onChange,
  onDelete,
  onReverse,
  onDragStart,
}: EdgePropertiesPanelProps) {
  // Cast data to EdgeData for type safety
  const edgeData = (edge.data || {}) as EdgeData

  // Local state for editable fields (HTML content for WYSIWYG)
  const [localLabelHtml, setLocalLabelHtml] = useState(edgeData.labelHtml || '')
  const [localDescriptionHtml, setLocalDescriptionHtml] = useState(edgeData.descriptionHtml || '')

  // Get source and target node info
  const sourceNode = useMemo(() => nodes.find((n) => n.id === edge.source), [nodes, edge.source])
  const targetNode = useMemo(() => nodes.find((n) => n.id === edge.target), [nodes, edge.target])

  // Extract current values with defaults
  const currentColor = edgeData.color || edge.style?.stroke as string || '#6b7280'
  const currentStrokeWidth = edgeData.strokeWidth || (edge.style?.strokeWidth as number) || 2
  const currentAnimated = edge.animated || edgeData.animated || false
  const currentType = (edge.type as EdgeTypeId) || 'default'
  const currentPathType = edgeData.pathType || 'bezier'

  // Parse marker types from edge
  const getMarkerType = (marker: Edge['markerEnd'] | Edge['markerStart']): MarkerOption => {
    if (!marker) return 'none'
    if (typeof marker === 'string') {
      return marker.toLowerCase().replace('marker', '') as MarkerOption
    }
    const type = marker.type?.toLowerCase() || 'none'
    if (type === 'arrowclosed') return 'arrowclosed'
    if (type === 'arrow') return 'arrow'
    return 'none'
  }

  const currentMarkerStart = getMarkerType(edge.markerStart)
  const currentMarkerEnd = getMarkerType(edge.markerEnd)

  // Update local state when edge changes
  useEffect(() => {
    setLocalLabelHtml(edgeData.labelHtml || '')
    setLocalDescriptionHtml(edgeData.descriptionHtml || '')
  }, [edgeData.labelHtml, edgeData.descriptionHtml])

  // Handlers - WYSIWYG editors pass both HTML and plain text
  const handleLabelChange = useCallback((html: string, plainText: string) => {
    setLocalLabelHtml(html)
    // edge.label = plain text (shown on canvas)
    // data.label = plain text (for persistence)
    // data.labelHtml = HTML (for WYSIWYG editing)
    onChange({
      label: plainText || undefined, // Plain text for canvas display
      data: {
        ...edgeData,
        label: plainText,
        labelHtml: html,
      },
    })
  }, [edgeData, onChange])

  const handleDescriptionChange = useCallback((html: string, plainText: string) => {
    setLocalDescriptionHtml(html)
    onChange({
      data: {
        ...edgeData,
        description: plainText,
        descriptionHtml: html,
      },
    })
  }, [edgeData, onChange])

  const handleTypeChange = useCallback((type: EdgeTypeId) => {
    onChange({ type })
  }, [onChange])

  const handleColorChange = useCallback((color: string) => {
    onChange({
      data: { ...edgeData, color },
      style: { ...edge.style, stroke: color },
    })
  }, [edgeData, edge.style, onChange])

  const handleStrokeWidthChange = useCallback((value: number[]) => {
    const strokeWidth = value[0]
    onChange({
      data: { ...edgeData, strokeWidth },
      style: { ...edge.style, strokeWidth },
    })
  }, [edgeData, edge.style, onChange])

  const handleAnimatedChange = useCallback((animated: boolean) => {
    onChange({
      animated,
      data: { ...edgeData, animated },
    })
  }, [edgeData, onChange])

  const handleMarkerStartChange = useCallback((marker: MarkerOption) => {
    if (marker === 'none') {
      onChange({ markerStart: undefined })
    } else {
      onChange({
        markerStart: {
          type: marker === 'arrowclosed' ? 'arrowclosed' as MarkerType : marker as MarkerType,
          color: currentColor,
        },
      })
    }
  }, [currentColor, onChange])

  const handleMarkerEndChange = useCallback((marker: MarkerOption) => {
    if (marker === 'none') {
      onChange({ markerEnd: undefined })
    } else {
      onChange({
        markerEnd: {
          type: marker === 'arrowclosed' ? 'arrowclosed' as MarkerType : marker as MarkerType,
          color: currentColor,
        },
      })
    }
  }, [currentColor, onChange])

  const handlePathTypeChange = useCallback((pathType: PathType) => {
    onChange({
      data: { ...edgeData, pathType },
    })
  }, [edgeData, onChange])

  const handleResetStyle = useCallback(() => {
    onChange({
      label: undefined, // Clear React Flow label
      data: {
        ...edgeData,
        color: undefined,
        strokeWidth: undefined,
        animated: false,
        label: undefined,
        labelHtml: undefined,
        description: undefined,
        descriptionHtml: undefined,
      },
      style: {},
      animated: false,
      markerStart: undefined,
      markerEnd: { type: 'arrowclosed' as MarkerType },
    })
    setLocalLabelHtml('')
    setLocalDescriptionHtml('')
  }, [edgeData, onChange])

  // Default open sections
  const defaultOpenSections = ['identification', 'appearance', 'connection']

  return (
    <div className="pointer-events-auto">
      {/* Header - draggable when onDragStart is provided */}
      <div
        className={cn(
          "p-3 border-b flex items-center justify-between",
          onDragStart && "cursor-grab active:cursor-grabbing select-none"
        )}
        onMouseDown={onDragStart}
      >
        <div>
          <h3 className="text-sm font-semibold">Propiedades del Edge</h3>
          <p className="text-[11px] text-muted-foreground">
            Conexión entre nodos
          </p>
        </div>
        <Route className="h-5 w-5 text-muted-foreground" />
      </div>

      <Accordion
        type="multiple"
        className="w-full"
        defaultValue={defaultOpenSections}
      >
        {/* Section 1: Identificación */}
        <AccordionItem value="identification" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Tag size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Identificación</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Etiqueta y descripción
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-3">
              {/* Label - WYSIWYG Rich Text Editor */}
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Etiqueta
                </Label>
                <RichTextEditor
                  value={localLabelHtml}
                  onChange={handleLabelChange}
                  placeholder="Nombre de la conexión..."
                  minHeight="50px"
                  maxHeight="100px"
                  compact
                />
              </div>

              {/* Description - WYSIWYG Rich Text Editor */}
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Descripción / Notas
                </Label>
                <RichTextEditor
                  value={localDescriptionHtml}
                  onChange={handleDescriptionChange}
                  placeholder="Añade notas sobre esta conexión..."
                  minHeight="70px"
                  maxHeight="130px"
                />
              </div>

              {/* Edge ID (Read-only) */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">ID del Edge</Label>
                <div className="text-[10px] text-muted-foreground bg-muted px-2 py-1.5 rounded-md font-mono truncate">
                  {edge.id}
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 2: Tipo de Edge */}
        <AccordionItem value="type" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Spline size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Tipo de Conexión</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Algoritmo de trazado
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-3">
              {/* Edge Type */}
              <div className="space-y-1.5">
                <Label className="text-xs">Tipo de Edge</Label>
                <Select value={currentType} onValueChange={(v) => handleTypeChange(v as EdgeTypeId)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {EDGE_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id} className="text-xs">
                        <div className="flex flex-col">
                          <span className="font-medium">{opt.name}</span>
                          <span className="text-[10px] text-muted-foreground">{opt.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Path Type (for reshapable edges) */}
              {currentType === 'reshapable' && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Algoritmo de Path</Label>
                  <Select value={currentPathType} onValueChange={(v) => handlePathTypeChange(v as PathType)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Seleccionar algoritmo" />
                    </SelectTrigger>
                    <SelectContent>
                      {PATH_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} className="text-xs">
                          <div className="flex flex-col">
                            <span className="font-medium">{opt.label}</span>
                            <span className="text-[10px] text-muted-foreground">{opt.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 3: Apariencia */}
        <AccordionItem value="appearance" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Palette size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Apariencia</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Color, grosor y animación
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-4">
              {/* Color Picker */}
              <div className="space-y-1.5">
                <Label className="text-xs">Color del Edge</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  {EDGE_COLORS.map((c) => (
                    <ColorSwatch
                      key={c.value}
                      color={c.value}
                      name={c.name}
                      selected={currentColor === c.value}
                      onClick={() => handleColorChange(c.value)}
                    />
                  ))}
                </div>
                {/* Custom color input */}
                <div className="flex items-center gap-2 mt-2">
                  <Label className="text-[10px] text-muted-foreground">Personalizado:</Label>
                  <input
                    type="color"
                    value={currentColor}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="h-6 w-10 rounded border cursor-pointer"
                  />
                  <span className="text-[10px] text-muted-foreground font-mono">{currentColor}</span>
                </div>
              </div>

              {/* Stroke Width */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Grosor de línea</Label>
                  <span className="text-[10px] text-muted-foreground">{currentStrokeWidth}px</span>
                </div>
                <Slider
                  value={[currentStrokeWidth]}
                  onValueChange={handleStrokeWidthChange}
                  min={1}
                  max={8}
                  step={0.5}
                  className="w-full"
                />
              </div>

              {/* Animated */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-3.5 w-3.5 text-muted-foreground" />
                  <Label className="text-xs">Animación</Label>
                </div>
                <Switch
                  checked={currentAnimated}
                  onCheckedChange={handleAnimatedChange}
                />
              </div>
              {currentAnimated && (
                <p className="text-[10px] text-muted-foreground italic ml-5">
                  La línea mostrará un efecto de flujo animado
                </p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 4: Marcadores (Flechas) */}
        <AccordionItem value="markers" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <ArrowRight size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Marcadores</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Flechas y terminaciones
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-3">
              {/* Marker Start */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <ArrowLeft className="h-3 w-3" />
                  Marcador Inicio
                </Label>
                <Select value={currentMarkerStart} onValueChange={(v) => handleMarkerStartChange(v as MarkerOption)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Ninguno" />
                  </SelectTrigger>
                  <SelectContent>
                    {MARKER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-xs">
                        <div className="flex items-center gap-2">
                          {opt.icon}
                          <span>{opt.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Marker End */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <ArrowRight className="h-3 w-3" />
                  Marcador Final
                </Label>
                <Select value={currentMarkerEnd} onValueChange={(v) => handleMarkerEndChange(v as MarkerOption)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Flecha cerrada" />
                  </SelectTrigger>
                  <SelectContent>
                    {MARKER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-xs">
                        <div className="flex items-center gap-2">
                          {opt.icon}
                          <span>{opt.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Visual Preview */}
              <div className="p-2 bg-muted/30 rounded-md">
                <p className="text-[10px] text-muted-foreground mb-2">Vista previa de dirección:</p>
                <div className="flex items-center justify-center gap-2 py-1">
                  {currentMarkerStart !== 'none' && (
                    <span className="text-xs">{MARKER_OPTIONS.find(m => m.value === currentMarkerStart)?.icon}</span>
                  )}
                  <div className="flex-1 h-0.5 rounded" style={{ backgroundColor: currentColor }} />
                  {currentMarkerEnd !== 'none' && (
                    <span className="text-xs">{MARKER_OPTIONS.find(m => m.value === currentMarkerEnd)?.icon}</span>
                  )}
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 5: Conexión (Read-only info) */}
        <AccordionItem value="connection" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Link2 size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Conexión</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Nodos origen y destino
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-3">
              {/* Source Node */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Nodo Origen</Label>
                <div className="p-2 border rounded-md bg-muted/30">
                  <div className="flex items-center gap-2">
                    {(sourceNode?.data as any)?.icon && (
                      <span className="text-sm">{(sourceNode?.data as any)?.icon}</span>
                    )}
                    <div className="flex flex-col">
                      <span className="text-xs font-medium">
                        {(sourceNode?.data as any)?.label || sourceNode?.id || 'Desconocido'}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        Tipo: {sourceNode?.type || 'N/A'}
                      </span>
                    </div>
                  </div>
                  {edge.sourceHandle && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Handle: {edge.sourceHandle}
                    </p>
                  )}
                </div>
              </div>

              {/* Target Node */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Nodo Destino</Label>
                <div className="p-2 border rounded-md bg-muted/30">
                  <div className="flex items-center gap-2">
                    {(targetNode?.data as any)?.icon && (
                      <span className="text-sm">{(targetNode?.data as any)?.icon}</span>
                    )}
                    <div className="flex flex-col">
                      <span className="text-xs font-medium">
                        {(targetNode?.data as any)?.label || targetNode?.id || 'Desconocido'}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        Tipo: {targetNode?.type || 'N/A'}
                      </span>
                    </div>
                  </div>
                  {edge.targetHandle && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Handle: {edge.targetHandle}
                    </p>
                  )}
                </div>
              </div>

              {/* Z-Index */}
              {edge.zIndex !== undefined && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Z-Index</Label>
                  <div className="text-[10px] text-muted-foreground bg-muted px-2 py-1 rounded">
                    {edge.zIndex}
                  </div>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 6: Acciones */}
        <AccordionItem value="actions" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <RotateCcw size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Acciones</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Invertir, reiniciar, eliminar
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-2">
              {/* Reverse Direction */}
              {onReverse && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start h-8 text-xs"
                  onClick={onReverse}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-2" />
                  Invertir Dirección
                </Button>
              )}

              {/* Reset Style */}
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start h-8 text-xs"
                onClick={handleResetStyle}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-2" />
                Reiniciar Estilo
              </Button>

              {/* Delete */}
              {onDelete && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full justify-start h-8 text-xs"
                  onClick={onDelete}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  Eliminar Edge
                </Button>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}
